// Hub Events page: list + create/edit/delete events that affect hub capacity.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, Spinner } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { fmtDate } from "../../lib/format";
import { effectiveCapacityPct } from "../../lib/forecast/engine";
import type { Customer, Hub, HubEvent } from "../../lib/types";
import { listHubs } from "../hubs/hubsApi";
import { listCustomers } from "../customers/customersApi";
import { deleteHubEvent, listHubEvents } from "./hubEventsApi";
import HubEventForm from "./HubEventForm";

export default function HubEventList() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [hubs, setHubs] = useState<Hub[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formFor, setFormFor] = useState<HubEvent | null | undefined>(undefined);

  const hubName = useMemo(() => {
    const m = new Map(hubs.map((h) => [h.id, h.name]));
    return (id: string) => m.get(id) ?? "?";
  }, [hubs]);

  const customerName = useMemo(() => {
    const m = new Map(customers.map((c) => [c.id, c.name]));
    return (id: string) => m.get(id) ?? "?";
  }, [customers]);

  const reload = useCallback(async () => {
    if (!accountId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [h, c, e] = await Promise.all([
        listHubs(accountId),
        listCustomers(accountId),
        listHubEvents(accountId),
      ]);
      setHubs(h);
      setCustomers(c);
      setEvents(e);
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to load hub events."));
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function remove(ev: HubEvent) {
    if (!accountId) return;
    if (!confirm(`Delete this ${ev.type} event at ${hubName(ev.hubId)}?`)) return;
    try {
      await deleteHubEvent(accountId, ev.id);
      await reload();
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to delete event."));
    }
  }

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Hub Events</h1>
        <Button onClick={() => setFormFor(null)} disabled={hubs.length === 0}>
          + New event
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card className="p-0">
        {loading ? (
          <Spinner />
        ) : events.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">
            No hub events. Closures, capacity changes, and reroutes you add here flow into the forecast.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3 font-medium">Hub</th>
                <th className="px-5 py-3 font-medium">Type</th>
                <th className="px-5 py-3 font-medium">Applies to</th>
                <th className="px-5 py-3 font-medium">Window</th>
                <th className="px-5 py-3 font-medium">Capacity</th>
                <th className="px-5 py-3 font-medium">Reroute</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-3 font-medium text-slate-900">{hubName(ev.hubId)}</td>
                  <td className="px-5 py-3 capitalize text-slate-600">{ev.type}</td>
                  <td className="px-5 py-3 text-slate-600">
                    {ev.mode === "by_customer" ? (
                      <span
                        title={(ev.customerOverrides ?? [])
                          .map((o) => `${customerName(o.customerId)} ${o.pct}%`)
                          .join(", ")}
                        className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
                      >
                        {(ev.customerOverrides ?? []).length === 1
                          ? `${customerName(ev.customerOverrides![0].customerId)} ${ev.customerOverrides![0].pct}%`
                          : `${(ev.customerOverrides ?? []).length} customers`}
                      </span>
                    ) : (
                      <span className="text-slate-400">Whole hub</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-600">
                    {fmtDate(ev.startDate)} → {fmtDate(ev.endDate)}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{effectiveCapacityPct(ev)}%</td>
                  <td className="px-5 py-3 text-slate-600">
                    {ev.redistributeVolume && ev.redistributeHubs?.length
                      ? ev.redistributeHubs.map((s) => `${hubName(s.hubId)} ${s.pct}%`).join(", ")
                      : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        ev.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {ev.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setFormFor(ev)}>
                        Edit
                      </Button>
                      <Button variant="danger" onClick={() => remove(ev)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {formFor !== undefined && (
        <HubEventForm
          key={formFor?.id ?? "new"}
          accountId={accountId}
          event={formFor}
          hubs={hubs}
          customers={customers}
          onClose={() => setFormFor(undefined)}
          onSaved={() => {
            setFormFor(undefined);
            reload();
          }}
        />
      )}
    </div>
  );
}
